import { completeStripePayment, getWebPurchaseDTO } from 'src/lib/iap/iap.util';
import StateObserver from 'src/StateObserver';
import {
  ProductId,
  STRIPE_PUBLISHABLE_KEY,
} from 'src/replicant/iap/iap.ruleset';
import { captureGenericError } from 'src/lib/sentry';
import type { Stripe } from '@stripe/stripe-js';
import { PurchaseCompleteResult, StripePaymentMethod } from '../iap.types';
import {
  getRevenueBasePrice,
  getRevenueGrossAnalytics,
} from '../analytics/iap.gross.revenue';

let stripeSdkCache: Promise<Stripe>;

/**
 * Get stripe instance
 */
export async function getStripe(): Promise<Stripe> {
  const { loadStripe } = await import('@stripe/stripe-js');

  return (stripeSdkCache ??= loadStripe(
    STRIPE_PUBLISHABLE_KEY,
  ) as Promise<Stripe>);
}

/**
 * Stripe finish purchase flow
 */
export async function stripeFinishPurchaseSequence(
  paymentMethod: StripePaymentMethod,
  productId: ProductId,
  purchaseID: string,
): Promise<PurchaseCompleteResult> {
  try {
    // Complete payment intent
    await completeStripePayment();

    // Payment completed, get purchase info
    return {
      response: getWebPurchaseDTO(productId),
      revenue: getRevenueGrossAnalytics(productId, {
        provider: 'stripe',
        providerMethod: paymentMethod,
        ...getRevenueBasePrice(productId),
      }),
      purchaseID,
    };
  } catch (e) {
    captureGenericError('Failed to complete payment intent', e);
    throw new Error('Failed to complete payment intent: ' + e);
  } finally {
    // Clear payment intent after purchase
    await StateObserver.invoke.clearStripePayment();
  }
}

/**
 * Create payment intent
 */
export async function stripeCreatePayment(productId: ProductId): Promise<{
  clientSecret: string;
  purchaseID: string;
}> {
  const result = await StateObserver.invoke.asyncCreateStripePayment({
    productId,
  });

  return { clientSecret: result.clientSecret, purchaseID: result.purchaseID };
}
